import type { OpenPosition } from '@flipster/r230522/original/position';

/**
 * 열린 포지션 테이블의 identity 안정화 로직 (dep-free 순수 모듈).
 * `openPositionsAtom`은 소켓 tick마다 모든 포지션 객체를 새로 만들기 때문에({...p} 스프레드),
 * 값이 실제로 같은 tick에는 이전 identity를 유지시켜 구독 전파를 끊는 용도로 사용한다.
 */

/** 행 하나를 식별하는 최소 키. Table 렌더 입력은 이 키 목록만 사용한다. */
export interface OpenPositionKey {
  symbol: string;
  slot: number;
}

export const toOpenPositionKeys = (positions: readonly Pick<OpenPosition, 'symbol' | 'slot'>[]): OpenPositionKey[] =>
  positions.map(({ symbol, slot }) => ({ symbol, slot }));

/** 키 집합·순서가 같으면 true → selectAtom이 이전 배열 identity를 유지해 Table 리렌더를 차단한다. */
export const areOpenPositionKeysEqual = (a: readonly OpenPositionKey[], b: readonly OpenPositionKey[]): boolean =>
  a.length === b.length && a.every((key, i) => key.symbol === b[i].symbol && key.slot === b[i].slot);

/**
 * 포지션 파생 객체의 얕은 값 비교.
 * - 원시 필드는 Object.is
 * - 배열 필드(tpOrders, slOrders 등)는 length + 요소 Object.is
 * 요소 객체가 재생성되면 false를 반환하므로(리렌더 1회) 정확성은 항상 보존된다 —
 * 비교가 실패해도 현재 동작(매 tick 리렌더)보다 나빠지지 않는다.
 */
export const isShallowStableEqual = <T extends object>(a: T | undefined, b: T | undefined): boolean => {
  if (Object.is(a, b)) return true;
  if (!a || !b) return false;

  const aKeys = Object.keys(a) as (keyof T)[];
  if (aKeys.length !== Object.keys(b).length) return false;

  return aKeys.every((key) => {
    const aValue = a[key];
    const bValue = b[key];
    if (Array.isArray(aValue) && Array.isArray(bValue)) {
      return aValue.length === bValue.length && aValue.every((value, i) => Object.is(value, bValue[i]));
    }
    return Object.is(aValue, bValue);
  });
};
